import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { DURATION_BUCKETS, PRICE_BUCKETS } from "@ckgo2/shared";
import {
  ClipboardList,
  MapPin,
  MailCheck,
  Trash2,
  TrendingUp,
  BarChart2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface QuizResult {
  id: string;
  answers: Record<string, string>;
  recommendedTripNames: string[];
  email?: string;
  subscribedNewsletter: boolean;
  createdAt: string;
}

interface QuizStats {
  totalQuizzes: number;
  newsletterSignups: number;
  signupRate: number;
  last30Days: number;
  topTrips: Array<{ id: string; name: string; count: number }>;
  answerDistribution: Record<string, Record<string, number>>;
}

interface PaginatedResponse {
  data: QuizResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Bucket labels derive from the single source of truth so the admin display
// never drifts from the public quiz options.
const ANSWER_LABELS: Record<string, Record<string, string>> = {
  type: {
    adventure: "Adventure",
    relax: "Relaxation",
    culture: "Culture",
    nature: "Nature",
  },
  budget: Object.fromEntries(PRICE_BUCKETS.map((b) => [b.key, b.label])),
  duration: Object.fromEntries(DURATION_BUCKETS.map((b) => [b.key, b.label])),
  companion: {
    solo: "Solo",
    couple: "Couple",
    family: "Family",
    friends: "Friends",
  },
  region: {
    northern: "Northern Vietnam",
    central: "Central Vietnam",
    southern: "Southern Vietnam",
    combination: "Combination",
  },
};

export default function QuizResultsPage() {
  const [page, setPage] = useState(1);
  const [showStats, setShowStats] = useState(true);
  const queryClient = useQueryClient();

  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ["quiz-results", page],
    queryFn: () =>
      api.get<ApiResponse<QuizResult[]> & { pagination: PaginatedResponse["pagination"] }>(
        `/api/admin/quiz/results?page=${page}&limit=20`,
      ),
  });

  const { data: statsData } = useQuery({
    queryKey: ["quiz-stats"],
    queryFn: () => api.get<ApiResponse<QuizStats>>("/api/admin/quiz/stats"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/quiz/results/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz-results"] });
      queryClient.invalidateQueries({ queryKey: ["quiz-stats"] });
    },
  });

  const results = resultsData?.data || [];
  const pagination = resultsData?.pagination;
  const stats = statsData?.data;

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this quiz result?")) {
      deleteMutation.mutate(id);
    }
  };

  const getAnswerLabel = (question: string, answer: string) => {
    return ANSWER_LABELS[question]?.[answer] || answer;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quiz Results</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            View trip quiz results and statistics
          </p>
        </div>
        <button onClick={() => setShowStats(!showStats)} className="btn-secondary">
          <BarChart2 className="h-5 w-5" />
          {showStats ? "Hide Stats" : "Show Stats"}
        </button>
      </div>

      {/* Stats */}
      {showStats && stats && (
        <div className="space-y-6">
          {/* Quick stats */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full p-2">
                  <ClipboardList className="text-primary-600 dark:text-primary-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Quizzes</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.totalQuizzes}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-success-100 dark:bg-success-900/30 rounded-full p-2">
                  <MailCheck className="text-success-600 dark:text-success-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Newsletter Signups</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.newsletterSignups} ({stats.signupRate}%)
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-accent-100 dark:bg-accent-900/30 rounded-full p-2">
                  <TrendingUp className="text-accent-600 dark:text-accent-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Last 30 Days</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.last30Days}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-warning-100 dark:bg-warning-900/30 rounded-full p-2">
                  <MapPin className="text-warning-600 dark:text-warning-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Top Trip</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.topTrips[0]?.name || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Top trips */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Most Recommended Trips
            </h2>
            <div className="space-y-3">
              {stats.topTrips.map((trip, index) => (
                <div key={trip.id} className="flex items-center gap-3">
                  <span className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">{trip.name}</span>
                      <span className="text-sm text-gray-500">{trip.count} times</span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className="bg-primary-500 h-full rounded-full"
                        style={{
                          width: `${(trip.count / stats.topTrips[0].count) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results table */}
      <div className="card overflow-hidden p-0">
        {resultsLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : results.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <ClipboardList className="mb-2 h-12 w-12 text-gray-300" />
            <p>No quiz results found</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Answers
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Recommended
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {results.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <span className="text-gray-900 dark:text-white">
                        {new Date(result.createdAt).toLocaleDateString("en-US")}
                      </span>
                      <br />
                      <span className="text-sm text-gray-500">
                        {new Date(result.createdAt).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(result.answers).map(([q, a]) => (
                          <span
                            key={q}
                            className="badge bg-gray-100 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            title={q}
                          >
                            {getAnswerLabel(q, a)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {result.recommendedTripNames.slice(0, 3).map((name) => (
                          <span key={name} className="badge-primary text-xs">
                            {name}
                          </span>
                        ))}
                        {result.recommendedTripNames.length > 3 && (
                          <span className="badge bg-gray-100 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            +{result.recommendedTripNames.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {result.email ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={`mailto:${result.email}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {result.email}
                          </a>
                          {result.subscribedNewsletter && (
                            <span title="Subscribed to newsletter">
                              <MailCheck className="text-success-500 h-4 w-4" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleDelete(result.id)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                  {pagination.total} results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="btn-ghost btn-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.totalPages}
                    className="btn-ghost btn-sm"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
